import { CalculateTotalOrder } from "../Calculates/CalculateTotalOrder"
import { ExtraServiceEntity } from "../ExtraService/ExtraServiceEntity"
import { converteDateToSql } from "../Generics/Helps"
import { ServicePoints } from "../ServicePoint/ServicePoints"
import { OrderCanceledEntity as OrderCanceledEntity } from "./OrderCanceledEntity"
import { CustomerEntity as CustomerEntity } from "../Customer/CustomerEntity"
import { OrderPaymentEntity } from "./OrderPaymentEntity"
import { OrderCatalogItem } from "./OrderCatalogItem"
import { OrderProduct } from "./OrderProduct"
import { BaseModel } from "../Generics/BaseModel"
import { EOrderStatusType } from "../../Enum/EOrderStatus"
import { EOrderType } from "../../Enum/EOrderType"
import { EOrderPlaceType } from "../../Enum/EOrderPlaceType"
import { AddressEntity } from "../Person/AddressEntity"
import { AwardEntity } from "../Awards/AwardEntity"
import { DeliveryEntity } from "../DeliveryEntity/DeliveryEntity"
import { OrderCatalogProduct } from "./OrderCatalogProduct"
import { CouponEntity } from "../Coupon/CouponEntity"

export class OrderEntity extends BaseModel {
  id: number

  customerId: number

  customerEntity: CustomerEntity

  servicePointId: number

  servicePointEntity: ServicePoints | null

  orderPaymentEntityId: number

  orderPaymentEntity: OrderPaymentEntity

  date: string

  createdAtUtc: string

  concurrencyToken: string

  isCancel: boolean

  orderCanceledId: number

  orderCanceledEntity?: OrderCanceledEntity

  orderCatalogItems: OrderCatalogItem[]

  orderProducts: OrderProduct[]

  extraServices: ExtraServiceEntity[]

  addressEntityId: number

  addressEntity: AddressEntity | null

  eOrderStatusType: EOrderStatusType

  status: EOrderType

  eOrderPlaceType: EOrderPlaceType

  isStorePickup: boolean

  selfServiceCustomerName: string

  selfServiceCustomerCpf: string

  canBeCanceledByServicePointSelfService: boolean

  createdByServicePointSelfService: boolean

  hasIssuedFiscalDocument: boolean

  ordersPaidIds: number[]

  orderExtraServicesIds: number[]

  CustomerEntity?: CustomerEntity

  associatedAwards: AwardEntity[]

  deliveryEntity?: DeliveryEntity

  calculateTotalOrder: CalculateTotalOrder

  couponEntity?: CouponEntity

  couponCode: string

  constructor(data: Partial<OrderEntity> = {}) {
    super()

    this.id = data.id ?? -1

    this.date = data.date ?? converteDateToSql(new Date())

    this.createdAtUtc = data.createdAtUtc ?? new Date().toISOString()

    this.concurrencyToken = data.concurrencyToken ?? ""

    this.eOrderStatusType = data.eOrderStatusType ?? EOrderStatusType.Pending

    this.status = data.status ?? EOrderType.Open

    this.eOrderPlaceType = data.eOrderPlaceType ?? EOrderPlaceType.Local

    this.isStorePickup = data.isStorePickup ?? false

    this.selfServiceCustomerName = data.selfServiceCustomerName ?? ""

    this.selfServiceCustomerCpf = data.selfServiceCustomerCpf ?? ""

    this.canBeCanceledByServicePointSelfService = data.canBeCanceledByServicePointSelfService ?? false

    this.createdByServicePointSelfService = data.createdByServicePointSelfService ?? false

    this.hasIssuedFiscalDocument = data.hasIssuedFiscalDocument ?? false

    this.calculateTotalOrder = new CalculateTotalOrder()

    this.customerId = data.customerId ?? data.customerEntity?.id ?? -1
    this.customerEntity = data.customerEntity ? new CustomerEntity(data.customerEntity) : null

    this.servicePointId = data.servicePointId ?? data.servicePointEntity?.id ?? -1
    this.servicePointEntity = data.servicePointEntity ? new ServicePoints(data.servicePointEntity) : null

    this.addressEntityId = data.addressEntityId ?? -1
    this.addressEntity = data.addressEntity ? new AddressEntity(data.addressEntity) : null

    this.orderCanceledId = data.orderCanceledId ?? -1
    this.isCancel = this.orderCanceledId !== -1

    this.orderCanceledEntity = data.orderCanceledEntity ? new OrderCanceledEntity(data.orderCanceledEntity) : null

    this.orderPaymentEntityId = data.orderPaymentEntityId ?? -1
    this.orderPaymentEntity = data.orderPaymentEntity ? new OrderPaymentEntity(data.orderPaymentEntity) : null

    this.associatedAwards = data.associatedAwards?.map((e) => new AwardEntity(e)) ?? []

    this.deliveryEntity = data.deliveryEntity ? new DeliveryEntity(data.deliveryEntity) : undefined

    this.extraServices = data.extraServices?.map((e) => new ExtraServiceEntity(e)) ?? []

    this.orderCatalogItems = data.orderCatalogItems?.map((e) => new OrderCatalogItem(e)) ?? []

    this.orderProducts = data.orderProducts?.map((e) => new OrderProduct(e)) ?? []

    this.ordersPaidIds = data.ordersPaidIds ?? []

    this.orderExtraServicesIds = data.orderExtraServicesIds ?? []

    this.couponEntity = data.couponEntity ? new CouponEntity(data.couponEntity) : null

    this.couponCode = data.couponCode ?? this.couponEntity?.code ?? ""
  }

  protected override getJsonFields(): string[] {
    return [
      "id",
      "customerId",
      "addressEntityId",
      "servicePointId",
      "date",
      "status",
      "eOrderPlaceType",
      "isStorePickup",
      "selfServiceCustomerName",
      "selfServiceCustomerCpf",
      "orderExtraServicesIds",
      "ordersPaidIds",
      "couponCode",
      "concurrencyToken",
      "orderPaymentEntity",
      "orderProducts",
      "orderCatalogItems",
    ]
  }

  override beforeToJSON(): void | Promise<void> {
    this.ConfigureToSendApi()

    this.orderCatalogItems.forEach((e) => e.beforeToJSON())
    this.orderProducts.forEach((e) => e.beforeToJSON())

    this.customerEntity?.awardManagers.forEach((e) => (e.awardEntity.awardManagerEntity = null))
  }

  isFinished() {
    return this.eOrderStatusType === EOrderStatusType.Delivered || this.status === EOrderType.Canceled
  }

  getCustomerDisplayName() {
    return this.selfServiceCustomerName || this.customerEntity?.personEntity?.name || ""
  }

  getCustomerDisplayCpf() {
    return this.selfServiceCustomerCpf || this.customerEntity?.personEntity?.cpf || ""
  }

  isServicePointSelfServiceOrder() {
    return Boolean(this.selfServiceCustomerName || this.selfServiceCustomerCpf)
  }

  getAllCatalogProducts(): OrderCatalogProduct[] {
    return [...this.orderProducts, ...this.orderCatalogItems]
  }

  ConfigureToSendApi() {
    this.date = converteDateToSql(new Date())

    this.orderExtraServicesIds = this.extraServices.map((e) => e.id)

    this.servicePointId = this.servicePointEntity?.id ?? 0

    this.orderCanceledId = this.orderCanceledEntity?.id ?? 0

    this.orderPaymentEntityId = this.orderPaymentEntity ? this.orderPaymentEntity.id : 0
  }
}
